import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

const noOperationAsyncFunction = async () => {};

const defaultUnreadContextValue = {
  unread: null,
  fetchUnread: noOperationAsyncFunction,
  markProjectsIndexSeen: noOperationAsyncFunction,
  markJobsIndexSeen: noOperationAsyncFunction,
  markProjectRead: noOperationAsyncFunction,
  markJobRead: noOperationAsyncFunction,
};

const UnreadContext = createContext(defaultUnreadContextValue);

export const useUnread = () => useContext(UnreadContext);

export default function UnreadProvider({ children, initialUnread }) {
  const [unread, setUnread] = useState(initialUnread);
  const unreadFetchAbortControllerReference = useRef(null);

  const applyUnread = useCallback((nextUnread) => {
    setUnread(nextUnread);
    window.dispatchEvent(
      new CustomEvent("unread:updated", { detail: nextUnread }),
    );
  }, []);

  const updateUnread = useCallback((updater) => {
    setUnread((currentUnread) => {
      const nextUnread =
        typeof updater === "function" ? updater(currentUnread) : updater;

      window.dispatchEvent(
        new CustomEvent("unread:updated", { detail: nextUnread }),
      );

      return nextUnread;
    });
  }, []);

  const getCsrfToken = () =>
    document.querySelector("meta[name='csrf-token']")?.content;

  const fetchUnread = useCallback(async () => {
    unreadFetchAbortControllerReference.current?.abort();

    const nextAbortController = new AbortController();
    unreadFetchAbortControllerReference.current = nextAbortController;

    try {
      const response = await fetch("/api/unread", {
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
        },
        signal: nextAbortController.signal,
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          applyUnread(null);
        }
        return;
      }

      const contentType = response.headers.get("content-type") || "";
      if (!contentType.includes("application/json")) return;

      let json;
      try {
        json = await response.json();
      } catch {
        return;
      }

      const nextUnread = {
        projectIndexUrl: json.project_index_url,
        jobIndexUrl: json.job_index_url,
        projectUnreadCount: json.project_unread_matched_count,
        jobUnreadCount: json.job_unread_matched_count,
        unreadProjects: json.projects,
        unreadJobs: json.jobs,
      };

      applyUnread(nextUnread);
    } catch (error) {
      if (error.name === "AbortError") {
        return;
      }
      console.error("Error while fetching unread:", error);
    }
  }, [applyUnread]);

  const markProjectsIndexSeen = useCallback(async () => {
    try {
      const response = await fetch("/api/projects/index_seen", {
        method: "POST",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": getCsrfToken(),
        },
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          return;
        }
        console.error(
          "Failed to mark projects index as seen:",
          response.status,
          response.statusText,
        );
        return;
      }

      updateUnread((currentUnread) => {
        if (!currentUnread) return currentUnread;

        return {
          ...currentUnread,
          projectUnreadCount: 0,
          unreadProjects: [],
        };
      });
    } catch (error) {
      console.error("Error while marking projects index as seen:", error);
    }
  }, [updateUnread]);

  const markJobsIndexSeen = useCallback(async () => {
    try {
      const response = await fetch("/api/jobs/index_seen", {
        method: "POST",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": getCsrfToken(),
        },
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          return;
        }
        console.error(
          "Failed to mark jobs index as seen:",
          response.status,
          response.statusText,
        );
        return;
      }

      updateUnread((currentUnread) => {
        if (!currentUnread) return currentUnread;

        return {
          ...currentUnread,
          jobUnreadCount: 0,
          unreadJobs: [],
        };
      });
    } catch (error) {
      console.error("Error while marking jobs index as seen:", error);
    }
  }, [updateUnread]);

  const markProjectRead = useCallback(
    async (projectId) => {
      try {
        const response = await fetch(`/api/projects/${projectId}/read`, {
          method: "POST",
          credentials: "same-origin",
          headers: {
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
            "X-CSRF-Token": getCsrfToken(),
          },
        });

        if (!response.ok) {
          if (response.status === 401 || response.status === 403) {
            return;
          }
          console.error(
            "Failed to mark project as read:",
            projectId,
            response.status,
            response.statusText,
          );
          return;
        }

        await fetchUnread();
      } catch (error) {
        console.error("Error while marking project as read:", projectId, error);
      }
    },
    [fetchUnread],
  );

  const markJobRead = useCallback(
    async (jobId) => {
      try {
        const response = await fetch(`/api/jobs/${jobId}/read`, {
          method: "POST",
          credentials: "same-origin",
          headers: {
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
            "X-CSRF-Token": getCsrfToken(),
          },
        });

        if (!response.ok) {
          if (response.status === 401 || response.status === 403) {
            return;
          }
          console.error(
            "Failed to mark job as read:",
            jobId,
            response.status,
            response.statusText,
          );
          return;
        }

        await fetchUnread();
      } catch (error) {
        console.error("Error while marking job as read:", jobId, error);
      }
    },
    [fetchUnread],
  );

  useEffect(() => {
    fetchUnread();

    return () => {
      unreadFetchAbortControllerReference.current?.abort();
    };
  }, [fetchUnread]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        fetchUnread();
      }
    };

    const handleWindowFocus = () => {
      fetchUnread();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleWindowFocus);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleWindowFocus);
    };
  }, [fetchUnread]);

  const value = {
    unread,
    fetchUnread,
    markProjectsIndexSeen,
    markJobsIndexSeen,
    markProjectRead,
    markJobRead,
  };

  return (
    <UnreadContext.Provider value={value}>
      {children}
    </UnreadContext.Provider>
  );
}